import liff from '@line/liff'
import { defineStore } from 'pinia'
import { sleep } from 'radash'
import { Component, markRaw, reactive, ref, Ref } from 'vue'

interface MemberInfo {
    customerName: string
    userId: string
    mobile: string
    lastSelfVerifyTime: string
    isConfirmInsuranceNewPolicy: boolean
    isSelfVerify: boolean
}

interface ErrorModalState {
    isShow: boolean
    title: string
    titleImage: string
    content: string
    component?: Component | null
    componentProps: Record<string, any> | null
    primaryWord: string
    primaryCallback?: () => void
    closeCallback?: () => void
}

export const useAppStore = defineStore('app', () => {
    const memberInfo: Ref<MemberInfo> = ref({
        customerName: '',
        userId: '',
        mobile: '',
        lastSelfVerifyTime: '',
        isConfirmInsuranceNewPolicy: false,
        isSelfVerify: false
    })

    const messageState = reactive({
        isShow: false,
        msg: ''
    })

    const isShowLoading = ref(false)
    const loadingCount = ref(0)
    const errorModal = ref<ErrorModalState>({
        isShow: false,
        title: '',
        content: '',
        titleImage: '',
        component: null,
        componentProps: null,
        primaryWord: '',
        primaryCallback: undefined as (() => void) | undefined,
        closeCallback: undefined as (() => void) | undefined
    })

    function showLoading() {
        loadingCount.value++
        isShowLoading.value = true
    }

    async function hideLoading(timeout?: number, isScrollTop = true) {
        if (timeout) await sleep(timeout)
        loadingCount.value > 0 && loadingCount.value--
        if (isScrollTop) window.scrollTo({ top: 0 })
        if (!loadingCount.value) isShowLoading.value = false
    }

    interface ErrorModalParams {
        title?: string
        titleImage?: string
        content?: string
        component?: Component | null
        componentProps?: Record<string, any> | null
        primaryWord?: string
        primaryCallback?: () => void
        closeCallback?: () => void
    }

    function showErrorModal(data: ErrorModalParams) {
        errorModal.value = {
            isShow: true,
            title: data.title || '查詢時出現錯誤',
            titleImage: data.titleImage || '',
            content: data.content || '若持續出現此問題，請聯繫資訊客服',
            component: data.component ? markRaw(data.component) : null,
            componentProps: data.componentProps ?? null,
            primaryWord: data.primaryWord || '',
            primaryCallback: data.primaryCallback || undefined,
            closeCallback: data.closeCallback || undefined
        }
    }

    function showApiErrorModal() {
        errorModal.value = {
            isShow: true,
            title: '查詢時出現錯誤',
            titleImage: '',
            content: '若持續出現此問題，請至幫助中心留言，將有專人為您服務',
            component: null,
            componentProps: null,
            primaryWord: '',
            primaryCallback: undefined,
            closeCallback: () =>
                liff.openWindow({ url: import.meta.env.VITE_LINE_FRIEND_LINK })
        }
    }

    function hideErrorModal() {
        errorModal.value.isShow = false
        errorModal.value.component = null
        errorModal.value.componentProps = null
        errorModal.value.primaryCallback = undefined
        errorModal.value.closeCallback = undefined
    }

    return {
        memberInfo,
        messageState,
        isShowLoading,
        errorModal,
        showLoading,
        hideLoading,
        showErrorModal,
        hideErrorModal,
        showApiErrorModal
    }
})
